import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [agencyName, setAgencyName] = useState(user?.agency_name || "");
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const logoUrl = user?.agency_logo_url ? `${process.env.REACT_APP_BACKEND_URL}${user.agency_logo_url}` : null;

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/profile", { name, agency_name: agencyName });
      await refreshUser();
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post("/profile/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Logo caricato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto">
      <h1 className="text-3xl font-extrabold text-navy tracking-tight mb-1">Profilo agenzia</h1>
      <p className="text-muted-foreground mb-8">Personalizza i dati e il logo che compaiono sulle offerte.</p>

      <div className="bg-white border border-border rounded-xl p-6 mb-6">
        <h2 className="font-extrabold text-navy mb-4 flex items-center gap-2"><Building2 size={18} className="text-primary" /> Logo agenzia</h2>
        <div className="flex items-center gap-6 flex-wrap">
          <div className="w-40 h-24 border border-dashed border-border rounded-lg flex items-center justify-center bg-secondary overflow-hidden">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="profile-logo-preview" /> : <span className="text-xs text-muted-foreground">Nessun logo</span>}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-button" className="gap-2 font-bold">
              <Upload size={16} /> {uploading ? "Caricamento..." : "Carica logo"}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">PNG, JPG o SVG. Consigliato sfondo trasparente.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-6">
        <h2 className="font-extrabold text-navy mb-4">Dati</h2>
        <div className="space-y-4">
          <div>
            <Label htmlFor="name">Il tuo nome</Label>
            <Input id="name" value={name} data-testid="profile-name-input" onChange={(e) => setName(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="agency">Nome agenzia</Label>
            <Input id="agency" value={agencyName} data-testid="profile-agency-input" onChange={(e) => setAgencyName(e.target.value)} placeholder="Es. Digital Agency Srl" className="mt-1.5" />
          </div>
          <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-button" className="font-bold">
            {saving ? "Salvataggio..." : "Salva profilo"}
          </Button>
        </div>
      </div>
    </div>
  );
}
